import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, CheckCircle2, ArrowRight, UserCheck, AlertCircle, Building2, User } from "lucide-react";
import { SignUpSchema } from '../lib/schemas';
import axios from 'axios';
import { useDropdown, useCities, PhoneField } from '../contexts/DropdownContext';
import { useAuth } from '../contexts/AuthContext';
import { useDataMode } from '../contexts/DataModeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTRACTOR_BENEFITS = [
  'Verified exhibitor briefs from 46+ countries',
  'Fair, transparent bidding system',
  'Build a certified contractor portfolio',
  'Secure payments & milestone tracking',
  'Manage all projects from one dashboard',
  'Priority support for verified contractors',
];

const ContractorSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const { login } = useAuth();
  const { isMockMode, toggleMode } = useDataMode();
  const { countries } = useDropdown();
  const [orgCountry, setOrgCountry] = useState('');
  const [phoneField, setPhoneField] = useState({ code: '+90', number: '' });
  const { cities } = useCities(orgCountry);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [inviteDetails, setInviteDetails] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  useEffect(() => {
    if (!inviteCode) return;
    axios.get(`${BACKEND_URL}/api/auth/invite/${inviteCode}`)
      .then(res => {
        setInviteDetails(res.data);
        if (res.data.email) {
          setFormData(f => ({ ...f, user: { ...f.user, email: res.data.email } }));
        }
      })
      .catch(() => setInviteError('Invalid or expired invite link. Please contact the team admin.'));
  }, [inviteCode]);

  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [formData, setFormData] = useState({
    role: 'CONTRACTOR',
    org: { display_name: '', country: '', city: '', website: '' },
    user: { name: '', email: '', phone_e164: '', password: '', confirmPassword: '' },
    accept_terms: false
  });

  const handleInputChange = (field, value, parentPath = []) => {
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (const path of parentPath) current = current[path];
      current[field] = value;
      return newData;
    });
    if (serverError) setServerError('');
  };

  const fillTestData = () => {
    setFormData({
      role: 'CONTRACTOR',
      org: { display_name: 'Prostand Display Solutions GmbH', country: 'DE', city: 'Munich', website: 'https://prostand-display.de' },
      user: { name: 'Michael Huber', email: 'm.huber@prostand-display.de', phone_e164: '', password: '123456Ab&', confirmPassword: '123456Ab&' },
      accept_terms: true,
    });
    setOrgCountry('DE');
    setBaseCurrency('EUR');
    setPhoneField({ code: '+49', number: '17612345679' });
    setErrors({});
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setServerError('');
    try {
      if (!phoneField.number || phoneField.number.length < 6) {
        setErrors(prev => ({ ...prev, 'user.phone_e164': 'Please enter a valid phone number (min 6 digits)' }));
        setLoading(false);
        return;
      }
      const formWithPhone = { ...formData, user: { ...formData.user, phone_e164: phoneField.code + phoneField.number } };

      let payload;
      if (inviteCode) {
        // Invite-based: skip org validation, just send user data + invite_code
        payload = {
          role: 'CONTRACTOR',
          user: { ...formWithPhone.user },
          accept_terms: true,
          invite_code: inviteCode,
          language_preference: localStorage.getItem('expostand_lang') || 'en',
          base_currency: baseCurrency,
        };
        delete payload.user.confirmPassword;
      } else {
        const validatedData = SignUpSchema.parse(formWithPhone);
        const { confirmPassword, ...userDataToSend } = validatedData.user;
        payload = { ...validatedData, user: userDataToSend, language_preference: localStorage.getItem('expostand_lang') || 'en', base_currency: baseCurrency };
      }

      await axios.post(`${BACKEND_URL}/api/auth/signup`, payload);
      // Auto-login after successful signup
      const result = await login(formData.user.email, formData.user.password);
      if (result.success) {
        if (isMockMode) toggleMode();
        // New contractor → onboarding wizard (unless already completed)
        const onboardingDone = localStorage.getItem('contractor_onboarding_done');
        navigate(onboardingDone ? '/app/contractor/dashboard' : '/app/contractor/onboarding');
      } else {
        navigate('/admin-login', { state: { email: formData.user.email, message: 'Account created! Please log in.' } });
      }
    } catch (error) {
      if (error.name === 'ZodError') {
        const fieldErrors = {};
        (error.issues || error.errors || []).forEach(err => { fieldErrors[err.path.join('.')] = err.message; });
        setErrors(fieldErrors);
      } else if (error.response?.data?.detail) {
        const d = error.response.data.detail;
        setServerError(typeof d === 'string' ? d : d.message || 'An error occurred during registration.');
      } else {
        setServerError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldError = (fieldPath) => errors[fieldPath];

  const inputClass = (err) =>
    `w-full bg-white border text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none transition-colors placeholder-gray-400 ${err ? 'border-red-400' : 'border-gray-200 focus:border-green-500'}`;

  return (
    <div className="min-h-screen flex bg-gray-950">
      {/* Left Panel — Green */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 30% 40%, #fff 1px, transparent 1px), radial-gradient(circle at 70% 60%, #fff 1px, transparent 1px)', backgroundSize:'40px 40px'}} />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2 text-white font-black text-xl tracking-tight">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-green-700 font-black text-sm">ES</span>
            </div>
            <span>EXPO STAND</span>
          </Link>
        </div>
        <div className="relative space-y-8">
          <div>
            <span className="text-green-300 text-xs font-bold uppercase tracking-widest">Contractor Portalı</span>
            <h1 className="text-4xl font-black text-white mt-2 leading-tight">
              Müşteri Tabanını<br /><span className="text-green-300">Dünyaya Yay</span>
            </h1>
            <p className="text-green-200 mt-4 text-sm leading-relaxed">
              Stand sektöründe nitelikli sergi projelerine teklif ver. Doğrulanmış müşteriler, şeffaf rekabet.
            </p>
          </div>
          <div className="space-y-3">
            {CONTRACTOR_BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-green-700 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-3 h-3 text-green-200" />
                </div>
                <span className="text-green-100 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <p className="text-green-300 text-xs">Zaten hesabın var mı?{' '}
            <button type="button" onClick={() => navigate('/contractor/login')} className="text-white font-semibold hover:underline">Giriş yap →</button>
          </p>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="w-full lg:w-1/2 flex items-start justify-center p-6 sm:p-10 overflow-y-auto bg-white">
        <div className="w-full max-w-md pt-8">
          <div className="mb-7">
            <div className="w-12 h-12 rounded-2xl bg-green-600 flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-black text-gray-900">Contractor Hesabı Oluştur</h1>
                <p className="text-gray-500 text-sm mt-1">Ücretsiz kaydol, proje kazan</p>
              </div>
              <button type="button" onClick={fillTestData}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-green-500/50 text-green-400 text-xs font-semibold hover:bg-green-900/30 transition-colors"
                data-testid="fill-test-data-btn">
                ⚡ Fill Test Data
              </button>
            </div>
          </div>

          {serverError && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {serverError}
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Invite Banner */}
            {inviteCode && (
              <div className={`rounded-xl p-4 border ${inviteError ? 'bg-red-900/30 border-red-800' : 'bg-green-900/30 border-green-700'}`}>
                {inviteError ? (
                  <div className="flex items-center gap-2 text-sm text-red-300"><AlertCircle size={16} className="shrink-0" />{inviteError}</div>
                ) : inviteDetails ? (
                  <div className="flex items-start gap-3">
                    <UserCheck size={18} className="text-green-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-green-100">Davet aldınız!</p>
                      <p className="text-xs text-green-300 mt-0.5">
                        <span className="font-semibold">{inviteDetails.org_name}</span> ekibine katılıyorsunuz
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-green-400">Davet detayları yükleniyor...</p>
                )}
              </div>
            )}

            {/* Company Info */}
            {!inviteCode && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-green-700 flex items-center justify-center">
                  <Building2 className="w-3 h-3 text-green-200" />
                </div>
                <span className="text-green-600 text-xs font-bold uppercase tracking-widest">Şirket Bilgileri</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Şirket Adı *</label>
                  <input
                    data-testid="input-company"
                    type="text"
                    placeholder="E.g. ABC Stand Construction"
                    value={formData.org.display_name}
                    onChange={(e) => handleInputChange('display_name', e.target.value, ['org'])}
                    className={inputClass(getFieldError('org.display_name'))}
                  />
                  {getFieldError('org.display_name') && <p className="text-red-500 text-xs mt-1">{getFieldError('org.display_name')}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Ülke *</label>
                  <select
                    data-testid="select-country"
                    value={formData.org.country}
                    onChange={e => { handleInputChange('country', e.target.value, ['org']); setOrgCountry(e.target.value); handleInputChange('city', '', ['org']); }}
                    className={inputClass(getFieldError('org.country'))}
                  >
                    <option value="">Select country</option>
                    {countries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                  </select>
                  {getFieldError('org.country') && <p className="text-red-500 text-xs mt-1">{getFieldError('org.country')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Şehir</label>
                  {cities.length > 0 ? (
                    <select
                      value={formData.org.city}
                      onChange={e => handleInputChange('city', e.target.value, ['org'])}
                      className={inputClass(getFieldError('org.city'))}
                    >
                      <option value="">Select city</option>
                      {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  ) : (
                    <input type="text" placeholder="Istanbul" value={formData.org.city}
                      onChange={(e) => handleInputChange('city', e.target.value, ['org'])}
                      className={inputClass(getFieldError('org.city'))} />
                  )}
                  {getFieldError('org.city') && <p className="text-red-500 text-xs mt-1">{getFieldError('org.city')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Website</label>
                  <input type="text" placeholder="www.sirketiniz.com" value={formData.org.website}
                    onChange={(e) => handleInputChange('website', e.target.value, ['org'])}
                    className={inputClass(false)} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Base Currency *</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { code: 'USD', symbol: '$', label: 'US Dollar' },
                      { code: 'EUR', symbol: '€', label: 'Euro' },
                      { code: 'GBP', symbol: '£', label: 'Pound' },
                      { code: 'TRY', symbol: '₺', label: 'Turkish Lira' },
                      { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham' },
                    ].map(c => (
                      <button key={c.code} type="button" data-testid={`currency-${c.code}`}
                        onClick={() => setBaseCurrency(c.code)}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border-2 text-xs font-semibold transition-all ${
                          baseCurrency === c.code ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500 hover:border-green-400'
                        }`}>
                        <span className="text-base leading-none mb-0.5">{c.symbol}</span>
                        <span>{c.code}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Your organization's default invoicing currency. You can still quote in other currencies.</p>
                </div>
              </div>
            </div>
            )} {/* end !inviteCode */}

            <div className="border-t border-gray-800" />

            {/* User Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center">
                  <User className="w-3 h-3 text-gray-300" />
                </div>
                <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">Kişisel Bilgiler</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Ad Soyad *</label>
                  <input data-testid="input-name" type="text" placeholder="Your Full Name"
                    value={formData.user.name}
                    onChange={(e) => handleInputChange('name', e.target.value, ['user'])}
                    className={inputClass(getFieldError('user.name'))} />
                  {getFieldError('user.name') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.name')}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">E-posta *</label>
                  <input data-testid="input-email" type="email" placeholder="isim@sirket.com"
                    value={formData.user.email}
                    onChange={(e) => handleInputChange('email', e.target.value, ['user'])}
                    className={inputClass(getFieldError('user.email'))} />
                  {getFieldError('user.email') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.email')}</p>}
                </div>
                <div className="col-span-2">
                  <PhoneField value={phoneField} onChange={setPhoneField} label="Phone" required />
                  {getFieldError('user.phone_e164') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.phone_e164')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Password *</label>
                  <div className="relative">
                    <input data-testid="input-password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                      value={formData.user.password}
                      onChange={(e) => handleInputChange('password', e.target.value, ['user'])}
                      className={inputClass(getFieldError('user.password')) + ' pr-10'} />
                    <button type="button" onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {getFieldError('user.password') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.password')}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Şifre Tekrar *</label>
                  <div className="relative">
                    <input data-testid="input-confirm-password" type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••"
                      value={formData.user.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value, ['user'])}
                      className={inputClass(getFieldError('user.confirmPassword')) + ' pr-10'} />
                    <button type="button" onClick={() => setShowConfirmPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {getFieldError('user.confirmPassword') && <p className="text-red-500 text-xs mt-1">{getFieldError('user.confirmPassword')}</p>}
                </div>
              </div>
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5 shrink-0">
                <input type="checkbox" data-testid="checkbox-terms"
                  checked={formData.accept_terms}
                  onChange={(e) => handleInputChange('accept_terms', e.target.checked)}
                  className="sr-only" />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  formData.accept_terms ? 'bg-green-600 border-green-600' : 'border-gray-300 group-hover:border-green-400'
                }`}>
                  {formData.accept_terms && (
                    <svg viewBox="0 0 10 8" className="w-3 h-2.5 fill-none stroke-white stroke-2">
                      <path d="M1 4l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-600 leading-relaxed">
                <a href="#" className="text-green-400 hover:underline font-medium">Kullanım Şartları</a> ve{' '}
                <a href="#" className="text-green-400 hover:underline font-medium">Gizlilik Politikası</a>'nı kabul ediyorum. *
              </span>
            </label>
            {getFieldError('accept_terms') && <p className="text-red-500 text-xs">{getFieldError('accept_terms')}</p>}

            {/* Submit */}
            <button type="submit" data-testid="submit-btn" disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all text-sm">
              {loading ? (
                <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Hesap oluşturuluyor...</>
              ) : (
                <>Hesap Oluştur <ArrowRight size={16} /></>
              )}
            </button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-6">
            Hesap oluşturarak şartlarımızı kabul etmiş olursunuz. Verileriniz GDPR kapsamında korunur.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContractorSignup;
